/**
 * Statistiche presenze (V34), la scheda «Presenze»: per giocatore gli
 * allenamenti svolti in cui era in rosa, presenze, assenze, assenze per
 * infortunio e non rilevati. Gli annullati non entrano nel conto. La
 * percentuale e sui soli rilevati: un appello dimenticato non diventa
 * un'assenza.
 *
 * In alto i numeri della squadra; sotto i giocatori in quattro card, una per
 * reparto come nella rosa, una sotto l'altra, con i giocatori su due colonne
 * quando c'e spazio. Ogni riga ha la maglia,
 * una barra con presenze, assenze, infortuni e non rilevati nei loro colori
 * e a destra la percentuale nel colore della sua fascia. Una tendina ordina
 * tutte le card insieme.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { RUOLI_GIOCATORE } from '@calcio/shared';
import {
  BandageIcon,
  CalendarXIcon,
  CheckIcon,
  CircleHelpIcon,
  PercentIcon,
  XIcon,
} from 'lucide-react';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { cn } from '@/lib/utils';
import { StatoCaricamento } from '../comuni/Testata.js';
import { Filtro } from '../comuni/FiltriVista.js';
import { useDati } from '../comuni/dati.js';
import { coloreValore } from '../giocatori/colori-stato.js';
import { REPARTI } from '../giocatori/elenchi.js';
import { voce } from '../navigazione/voci.js';
import {
  PaginaStatistiche,
  Riquadro,
  RigaGiocatore,
  Sezione,
  Tassello,
  Voce,
} from './PaginaStatistiche.js';
import { Maglia } from '../comuni/Maglia.js';
import {
  type Ordine,
  type RigaPresenze,
  type StatistichePresenze as Dati,
  numero,
  ordina,
  partecipazione,
  percentuale,
} from './statistiche.js';
import { Nota } from '../comuni/Nota.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

type Colonna = 'numero' | 'percentuale' | 'presenti' | 'assenti' | 'nonRilevati';

const ORDINI: Record<string, { etichetta: string; ordine: Ordine<Colonna> }> = {
  cognome: { etichetta: 'Cognome', ordine: { colonna: 'giocatore', verso: 'asc' } },
  numero: { etichetta: 'Numero di maglia', ordine: { colonna: 'numero', verso: 'asc' } },
  percentuale: { etichetta: 'Percentuale', ordine: { colonna: 'percentuale', verso: 'desc' } },
  presenti: { etichetta: 'Presenze', ordine: { colonna: 'presenti', verso: 'desc' } },
  assenti: { etichetta: 'Assenze', ordine: { colonna: 'assenti', verso: 'desc' } },
  nonRilevati: { etichetta: 'Non rilevati', ordine: { colonna: 'nonRilevati', verso: 'desc' } },
};

/** Le quattro voci del conto, nei colori degli stati: verde, rosso, arancio e grigio. */
const PARTI = [
  {
    chiave: 'presenti',
    etichetta: 'Presenze',
    Icona: CheckIcon,
    testo: 'text-stato-disponibile',
    fondo: 'bg-stato-disponibile',
  },
  {
    chiave: 'assenti',
    etichetta: 'Assenze',
    Icona: XIcon,
    testo: 'text-evento-cattivo',
    fondo: 'bg-evento-cattivo',
  },
  {
    chiave: 'infortunati',
    etichetta: 'Infortunio',
    Icona: BandageIcon,
    testo: 'text-stato-recupero',
    fondo: 'bg-stato-recupero',
  },
  {
    chiave: 'nonRilevati',
    etichetta: 'Non rilevati',
    Icona: CircleHelpIcon,
    testo: 'text-muted-foreground',
    fondo: 'bg-muted-foreground/40',
  },
] as const;

export function StatistichePresenze() {
  const { ss } = useParams<{ ss: string }>();
  const { dati, errore } = useDati<Dati>(`/squadre-stagione/${ss}/statistiche/presenze`);
  const [ordinePer, setOrdinePer] = useState('cognome');

  const ordinaPer = (
    <Filtro
      etichetta="Ordina per"
      valore={ordinePer}
      onCambia={setOrdinePer}
      opzioni={Object.entries(ORDINI).map(([valore, { etichetta }]) => ({ valore, etichetta }))}
    />
  );

  return (
    <PaginaStatistiche ss={ss!} scheda="presenze" filtri={dati && dati.svolti > 0 && ordinaPer}>
      {!dati ? (
        <StatoCaricamento errore={errore} />
      ) : dati.svolti === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nessun allenamento svolto</EmptyTitle>
            <EmptyDescription>
              Le presenze si contano sugli allenamenti svolti: la prima presenza dell'appello lo
              segna svolto.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <Contenuto dati={dati} ordine={(ORDINI[ordinePer] ?? ORDINI.cognome!).ordine} />
      )}
    </PaginaStatistiche>
  );
}

const NEUTRA = 'size-4 shrink-0 text-evento-neutro';

function Contenuto({ dati, ordine }: { dati: Dati; ordine: Ordine<Colonna> }) {
  const IconaAllenamenti = voce('allenamenti').icona;
  const globale = partecipazione(dati.giocatori);
  const nonRilevati = dati.giocatori.reduce((t, g) => t + g.nonRilevati, 0);
  const ordinati = ordina(dati.giocatori, ordine);

  return (
    <div className="flex flex-col gap-4">
      <Sezione titolo="Allenamenti" icona={<IconaAllenamenti aria-hidden className="size-5" />}>
        <div className="grid grid-cols-2 gap-2 @3xl:grid-cols-4">
          <Riquadro
            etichetta="Allenamenti svolti"
            icona={<IconaAllenamenti aria-hidden className={NEUTRA} />}
            valore={numero(dati.svolti)}
          />
          <Riquadro
            etichetta="Annullati"
            icona={<CalendarXIcon aria-hidden className={NEUTRA} />}
            valore={numero(dati.annullati)}
            sotto="Fuori dal conteggio"
          />
          <Riquadro
            etichetta="Partecipazione"
            icona={<PercentIcon aria-hidden className={NEUTRA} />}
            valore={percentuale(globale)}
            sotto="Presenti sui rilevati"
          />
          <Riquadro
            etichetta="Non rilevati"
            icona={<CircleHelpIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />}
            valore={numero(nonRilevati)}
            sotto="Appelli senza il giocatore"
          />
        </div>
      </Sezione>

      <div className="flex flex-col gap-4">
        {RUOLI_GIOCATORE.map((ruolo, i) => {
          const gruppo = ordinati.filter((g) => g.ruolo === ruolo);
          return (
            <Sezione key={ruolo} titolo={REPARTI[i]!} conteggio={gruppo.length}>
              {gruppo.length > 0 ? (
                <ul className="grid gap-2 @5xl:grid-cols-2">
                  {gruppo.map((g) => (
                    <li key={g.id}>
                      <Riga g={g} />
                    </li>
                  ))}
                </ul>
              ) : (
                <StatoVuoto titolo="Nessuno in rosa" />
              )}
            </Sezione>
          );
        })}
      </div>

      <Nota>
        Disponibili: allenamenti svolti in cui il giocatore era in rosa. Non rilevato: l'appello non
        lo ha registrato, e la percentuale non lo conta.
      </Nota>
    </div>
  );
}

function Riga({ g }: { g: RigaPresenze }) {
  return (
    <RigaGiocatore id={g.id}>
      <Maglia ruolo={g.ruolo} numero={g.numero} />
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-base font-semibold break-words">
            {g.cognome} {g.nome}
          </span>
          <span className="text-sm text-muted-foreground tabular-nums">
            {g.disponibili === 1 ? '1 disponibile' : `${g.disponibili} disponibili`}
          </span>
        </span>
        {g.disponibili > 0 && (
          <span className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
            {PARTI.map((p) => (
              <span
                key={p.chiave}
                className={p.fondo}
                style={{ width: `${(g[p.chiave] / g.disponibili) * 100}%` }}
              />
            ))}
          </span>
        )}
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          {PARTI.map((p) => (
            <Voce
              key={p.chiave}
              titolo={p.etichetta}
              icona={<p.Icona aria-label={p.etichetta} className={cn('size-4', p.testo)} />}
              valore={g[p.chiave]}
            />
          ))}
        </span>
      </span>
      <Tassello
        titolo="Presenze sui rilevati"
        icona={<PercentIcon aria-hidden className="size-3.5" />}
        colore={g.percentuale != null ? coloreValore(g.percentuale) : undefined}
        valore={g.percentuale == null ? '—' : Math.round(g.percentuale)}
      />
    </RigaGiocatore>
  );
}
